import { Loader2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ArrowRightIcon } from "@/components/icons"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { ContractsDialogSuccess } from "@/components/contracts/modals/contracts-dialog-success"
import { useContractMutations } from "@/hooks/contracts/useContracts"
import { cn } from "@/lib/utils"
import type { Contract } from "@/types/contract"
import {
  SherpaDialog,
  SherpaDialogContent,
  SherpaDialogDescription,
  SherpaDialogFooter,
  SherpaDialogHeader,
  SherpaDialogTitle,
} from "@/components/ui-kit/dialog"

type Step = "confirm" | "success"

export function ContractsRentEndDialog({
  open,
  onOpenChange,
  contract,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contract: Contract
}) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>("confirm")

  const { requestEnd } = useContractMutations()

  const handleOpenChange = (nextOpen: boolean) => {
    // Closed mid-request, the dialog would reopen on the success step, or let the request go twice.
    if (!nextOpen && requestEnd.isPending) {
      return
    }
    if (!nextOpen) {
      setStep("confirm")
    }
    onOpenChange(nextOpen)
  }

  const handleConfirm = () => {
    requestEnd.mutate(
      { contractId: contract.id, data: {} },
      { onSuccess: () => setStep("success") },
    )
  }

  const handleClose = () => {
    handleOpenChange(false)
  }

  return (
    <SherpaDialog open={open} onOpenChange={handleOpenChange}>
      <SherpaDialogContent
        showCloseButton={step === "confirm"}
        className={cn(
          "max-sm:left-0 max-sm:h-auto max-sm:max-h-[95dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 h-auto",
          step === "success" && "pt-8 sm:pt-10",
        )}
      >
        {step === "confirm" ? (
          <>
            <SherpaDialogHeader className="sm:gap-6 gap-3 mb-6">
              <SherpaDialogTitle>{t("contracts.rentEnd.title")}</SherpaDialogTitle>
              <SherpaDialogDescription>
                {t("contracts.rentEnd.description")}
              </SherpaDialogDescription>
            </SherpaDialogHeader>

            <ol className="flex list-none flex-col gap-6 [counter-reset:item] mb-12">
              {(t("contracts.rentEnd.steps", { returnObjects: true }) as string[]).map((stepText) => (
                <li
                  key={stepText}
                  className={cn(
                    "flex items-center gap-4 sm:text-[18px] text-[16px] sm:leading-[24px] leading-[22px] text-[#2b2e34] max-w-[450px]",
                    "before:flex before:size-[48px] before:shrink-0 before:items-center before:justify-center before:rounded-full before:bg-muted sm:before:text-[18px] text-[16px] sm:before:leading-[24px] leading-[22px] before:font-medium before:text-[#2b2e34] before:content-[counter(item)] [counter-increment:item]"
                  )}
                >
                  {stepText}
                </li>
              ))}
            </ol>

            <SherpaDialogFooter className="flex-col gap-4 sm:flex-col sm:justify-start pt-[10px]">
              <UiKitButton
                type="button"
                className="h-[60px] w-full"
                endIcon={requestEnd.isPending ? <Loader2 className="size-6 animate-spin" /> : <ArrowRightIcon size={24} />}
                disabled={requestEnd.isPending}
                onClick={handleConfirm}
              >
                {t("contracts.rentEnd.confirm")}
              </UiKitButton>
            </SherpaDialogFooter>
          </>
        ) : (
          <ContractsDialogSuccess
            title={t("contracts.rentEnd.successTitle")}
            description={t("contracts.rentEnd.successDescription")}
            iconWrapperClassName="pt-0"
            onClose={handleClose}
          />
        )}
      </SherpaDialogContent>
    </SherpaDialog>
  )
}
